/**
 * Telefon numarası formatlama ve validasyon
 * Evolution API formatı: 905XXXXXXXXX (ülke kodu + numara, boşluksuz)
 */

/**
 * Telefon numarasını temizle — sadece rakamları al
 */
export const cleanPhone = (phone: string): string => {
  return phone.replace(/[^0-9]/g, '');
};

/**
 * Türk telefon numarasını normalize et → 905XXXXXXXXX formatına çevir
 * Kabul edilen girişler:
 *   507911627    → 90507911627 (eksik alan kodu)  -- HATA: 9 hane yeterli değil
 *   5079116270   → 905079116270
 *   05079116270  → 905079116270
 *   905079116270 → 905079116270
 *   +905079116270→ 905079116270
 *   +90 507 911 62 70 → 905079116270
 */
export const normalizePhone = (phone: string): string => {
  let digits = cleanPhone(phone);

  // +90 veya 90 ile başlıyorsa kaldır
  if (digits.startsWith('90') && digits.length >= 12) {
    digits = digits.substring(2);
  }
  // Başında 0 varsa kaldır (05xx → 5xx)
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }
  // Şimdi 10 haneli olmalı (5XXXXXXXXX)
  return '90' + digits;
};

/**
 * Telefon numarasının geçerli olup olmadığını kontrol et
 * Geçerli: en az 10 hane (ülke kodu hariç), 5 ile başlamalı
 */
export const validatePhone = (phone: string): { valid: boolean; error?: string; formatted: string } => {
  let digits = cleanPhone(phone);

  if (digits.length === 0) {
    return { valid: false, error: 'Telefon numarası gereklidir', formatted: '' };
  }

  // +90 veya 90 ile başlıyorsa kaldır
  if (digits.startsWith('90') && digits.length >= 12) {
    digits = digits.substring(2);
  }
  // Başında 0 varsa kaldır
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }

  // 10 hane olmalı (5XX XXX XX XX)
  if (digits.length < 10) {
    return { valid: false, error: `Telefon numarası eksik (${digits.length}/10 hane)`, formatted: '90' + digits };
  }

  if (digits.length > 10) {
    return { valid: false, error: 'Telefon numarası çok uzun', formatted: '90' + digits.substring(0, 10) };
  }

  // Türk cep numarası 5 ile başlamalı
  if (!digits.startsWith('5')) {
    return { valid: false, error: 'Geçerli bir cep telefonu numarası girin (5XX ile başlamalı)', formatted: '90' + digits };
  }

  return { valid: true, formatted: '90' + digits };
};

/**
 * Telefon numarasını görüntüleme formatına çevir: +90 5XX XXX XX XX
 */
export const formatPhoneDisplay = (phone: string): string => {
  const normalized = normalizePhone(phone);
  const digits = normalized.replace(/^90/, '');
  if (digits.length !== 10) return phone;
  return `+90 ${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6, 8)} ${digits.substring(8, 10)}`;
};
